import React, { useState } from 'react';
import Papa from 'papaparse';
import axios from 'axios';
import GLOBALVAR from '../constants';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Toast, ToastContainer } from 'react-bootstrap';

const ImportLoanProcessing = () => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [isImporting, setIsImporting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ type: '', message: '' });

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setErrors([]);
    setProgress(0);
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage({ type, message });
    setShowToast(true);
  };

  const handleImport = () => {
    if (!file) {
      showNotification('Please select a file first!', 'error');
      return;
    }

    setIsImporting(true);
    setErrors([]);
    setProgress(0);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const data = results.data;
        const totalRows = data.length;
        const newErrors = [];

        for (let i = 0; i < data.length; i++) {
          const row = data[i];
          try {
            // Check if the applicant exists in the Applications database
            const checkResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applications/check`, {
              params: {
                firstName: row['First Name'],
                lastName: row['Last Name']
              }
            });

            if (checkResponse.data.exists) {
              const applicantID = checkResponse.data.id;

              // Check if there are existing loan processing records for this applicant
              const existingLoanResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/check`, {
                params: {
                  applicantID: applicantID,
                  dateGranted: row['Loan Date'] || '1900-01-01',
                  amountGranted: parseFloat(row['Loan Amount']) || 0
                }
              });

              // Determine loanProcessingType based on existing records
              const loanProcessingType = existingLoanResponse.data.exists && !existingLoanResponse.data.match ? 'reloan' : 'new';

              // Prepare the payload for the API
              const payload = {
                loanProcessingType: loanProcessingType,
                amountGranted: parseFloat(row['Loan Amount']) || 0,
                dateGranted: row['Loan Date'] ? new Date(row['Loan Date']).toISOString() : null,
                interestRate: parseFloat(((parseFloat(row['Interest Amount Per Month']) || 0) / 
                              (parseFloat(row['Loan Amount']) || 1) * 100).toFixed(2)),
                loanTerm: parseInt(row['Payment Terms (Months)']) || 0,
                paymentType: 'add',
                loanInterval: 'monthly',
                serviceFee: 100,
                notarialFee: 300,
                docStamp: parseFloat(((parseFloat(row['Loan Amount']) || 0) / 200 * 1.5).toFixed(2)),
                insurance: parseFloat(((parseFloat(row['Loan Amount']) || 0) / 1000 * 10).toFixed(2)),
                accountOfficer: 'Rouie',
                loanProduct: 'Personal Loan',
                loanLedger: 'HLC',
                loanSecurity: 'No Collateral',
                reference: row['Referral'] || '',
                loanType: 'personal',
                addtl_beneficiary: '',
                addtl_contact: '',
                addtl_relationship: '',
                is_deleted: false,
                applicantID: applicantID
            };
            
            // Validate required fields before sending
            if (!payload.dateGranted || !payload.amountGranted || !payload.interestRate || !payload.loanTerm) {
              newErrors.push('Missing required loan details');
            }

              if (existingLoanResponse.data.match) {
                // Update existing loan processing entry if dateGranted and amount match
                const updateResponse = await axios.post(
                  `${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/update/${existingLoanResponse.data.id}`,
                  payload
                );
                console.log('Loan processing entry updated successfully!', updateResponse.data);
              } else {
                // Create new loan processing entry
                const createResponse = await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/add`, payload);
                console.log('Loan processing entry added successfully!', createResponse.data);
              }
            } else {
              newErrors.push(`Row ${i + 1}: Applicant ${row['First Name']} ${row['Last Name']} not found.`);
            }
          } catch (error) {
            newErrors.push(`Row ${i + 1}: ${error.message}`);
          }
          
          setProgress(((i + 1) / totalRows) * 100);
        }

        setIsImporting(false);
        setErrors(newErrors);
        
        if (newErrors.length === 0) {
          showNotification('CSV import completed successfully!');
        } else {
          showNotification(`Import completed with ${newErrors.length} errors`, 'warning');
        }
      },
      error: (error) => {
        setIsImporting(false);
        showNotification(`Error parsing CSV: ${error.message}`, 'error');
      }
    });
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card shadow">
            <div className="card-body">
              <h2 className="card-title text-center mb-4">Import Loan Processing Data</h2>
              
              <div className="mb-4">
                <label className="form-label">Choose CSV File</label>
                <div className="input-group">
                  <input
                    type="file"
                    className="form-control"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={isImporting}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleImport}
                    disabled={!file || isImporting}
                  >
                    {isImporting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Importing...
                      </>
                    ) : (
                      'Import CSV'
                    )}
                  </button>
                </div>
              </div>

              {isImporting && (
                <div className="mb-4">
                  <div className="progress">
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated"
                      role="progressbar"
                      style={{ width: `${progress}%` }}
                      aria-valuenow={progress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      {Math.round(progress)}%
                    </div>
                  </div>
                </div>
              )}

              {errors.length > 0 && (
                <div className="alert alert-warning mt-3">
                  <h6>Import Errors:</h6>
                  <ul className="mb-0">
                    {errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)}
          bg={toastMessage.type === 'error' ? 'danger' : toastMessage.type}
          delay={3000}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Import Status</strong>
          </Toast.Header>
          <Toast.Body className={toastMessage.type === 'error' ? 'text-white' : ''}>
            {toastMessage.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default ImportLoanProcessing;